/**
 * Build an HTML representation of paste-anywhere math text where every X⁄Y fraction
 * (U+2044 FRACTION SLASH) becomes a proper <sup>num</sup>&frasl;<sub>den</sub> block —
 * which most rich-text contexts (Gmail, Slack, Discord, Word, Notion, comments) render
 * as a true visually-stacked fraction.
 *
 * Returns a HTML string. Plain (non-fraction) content passes through verbatim,
 * HTML-escaped to be safe.
 *
 * Pre-baked vulgar fractions (½ ⅓ ¼ etc) are passed through as-is since they're
 * already single-glyph stacked.
 */

const escapeHtml = (s) =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

const isWordBreak = (c) => !c || /[\s+\-=<>,;]/.test(c);

export function toHtmlFraction(text) {
  if (!text) return "";
  const s = text;
  let html = "";
  let i = 0;
  // Helper: walk back for numerator start, supports (a+b)
  const scanNumStart = (end) => {
    if (end <= 0) return 0;
    if (s[end - 1] === ")") {
      let depth = 1;
      for (let k = end - 2; k >= 0; k--) {
        if (s[k] === ")") depth++;
        else if (s[k] === "(") { depth--; if (depth === 0) return k; }
      }
    }
    let k = end;
    while (k > 0 && !isWordBreak(s[k - 1])) k--;
    return k;
  };
  const scanDenEnd = (start) => {
    if (start >= s.length) return s.length;
    if (s[start] === "(") {
      let depth = 1;
      for (let k = start + 1; k < s.length; k++) {
        if (s[k] === "(") depth++;
        else if (s[k] === ")") { depth--; if (depth === 0) return k + 1; }
      }
    }
    let k = start;
    while (k < s.length && !isWordBreak(s[k])) k++;
    return k;
  };
  const unwrap = (str) => {
    if (str.length >= 2 && str[0] === "(" && str[str.length - 1] === ")") {
      let depth = 0;
      for (let k = 0; k < str.length; k++) {
        if (str[k] === "(") depth++;
        else if (str[k] === ")") {
          depth--;
          if (depth === 0 && k < str.length - 1) return str;
        }
      }
      return str.slice(1, -1);
    }
    return str;
  };

  while (i < s.length) {
    const idx = s.indexOf("\u2044", i);
    if (idx === -1) { html += escapeHtml(s.slice(i)); break; }
    const nStart = scanNumStart(idx);
    const dEnd = scanDenEnd(idx + 1);
    if (nStart > i) html += escapeHtml(s.slice(i, nStart));
    const num = unwrap(s.slice(nStart, idx));
    const den = unwrap(s.slice(idx + 1, dEnd));
    if (num && den) {
      // Inline styles ensure the fraction stacks visually in apps that strip class names.
      // line-height:0 prevents extra vertical gap from sup/sub raising the line.
      html += `<span style="display:inline-block;vertical-align:middle;line-height:1;text-align:center;font-family:inherit"><span style="display:block;font-size:0.85em;border-bottom:1px solid currentColor;padding:0 0.2em">${escapeHtml(num)}</span><span style="display:block;font-size:0.85em;padding:0 0.2em">${escapeHtml(den)}</span></span>`;
    } else {
      html += escapeHtml(s.slice(nStart, dEnd));
    }
    i = dEnd;
  }
  return html;
}
